import React from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { Shield as ShieldIcon } from '@mui/icons-material';
import { TrustScoreReadReasonDialog } from './TrustScoreReadReasonDialog';
import { TrustScorePanel } from './TrustScorePanel';

// FEAT-043-admin-web (MVP-8.9, MVP-18.24).
//
// Collapsed-by-default Trust Score entry point that lives inside the
// admin-web user detail surface (`UserEditSidebar` drawer). It does
// NOT issue any trust-score request on mount. Clicking the "View trust
// score" button opens the read-reason dialog; on submit the panel
// mounts inline with the captured reason. The reason persists in the
// component state only while this section is mounted — closing the
// drawer unmounts the section and any further view requires a fresh
// read-reason (per the privacy rule "no cache, fresh prompt on every
// mount" documented in artifacts.yaml).

export interface TrustScoreSectionProps {
  userId: string;
  userDisplayName: string;
  userRole: 'PATIENT' | 'CAREGIVER' | 'ADMIN';
}

export const TrustScoreSection: React.FC<TrustScoreSectionProps> = ({
  userId,
  userDisplayName,
  userRole,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [activeReason, setActiveReason] = React.useState<string | null>(null);
  const [dialogError, setDialogError] = React.useState<string | null>(null);

  // Admins are excluded — trust score is computed for non-admin users
  // whose behaviour we may need to investigate.
  if (userRole === 'ADMIN') {
    return (
      <Box data-testid="trust-score-section-admin-noop">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <ShieldIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Trust Score</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Trust scores are not tracked for admin users.
        </Typography>
      </Box>
    );
  }

  const handleOpenDialog = () => {
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSubmitReason = (reason: string) => {
    setActiveReason(reason);
    setDialogOpen(false);
  };

  const handleCancelReason = () => {
    setDialogOpen(false);
  };

  return (
    <Box data-testid="trust-score-section">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ShieldIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Trust Score</Typography>
      </Stack>

      {activeReason ? (
        <TrustScorePanel userId={userId} reason={activeReason} />
      ) : (
        <Stack spacing={1}>
          <Alert severity="info" icon={false} variant="outlined">
            <Typography variant="body2" gutterBottom>
              Trust Score is admin-only and audit-logged on every read. Enter a
              reason before viewing.
            </Typography>
          </Alert>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ShieldIcon />}
              onClick={handleOpenDialog}
              data-testid="trust-score-open"
            >
              View trust score
            </Button>
          </Box>
        </Stack>
      )}

      <TrustScoreReadReasonDialog
        open={dialogOpen}
        targetUserName={userDisplayName}
        errorMessage={dialogError}
        onSubmit={handleSubmitReason}
        onCancel={handleCancelReason}
      />
    </Box>
  );
};

export default TrustScoreSection;
